import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Briefcase,
  ArrowLeft,
  MapPin,
  Globe,
  Users,
  Heart,
  Zap,
  Shield,
  TrendingUp,
  Coffee,
  Laptop,
  Building2,
  ChevronRight,
} from "lucide-react";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

export default function Careers() {
  const { t } = useTranslation();

  const benefits = [
    { icon: Globe, title: "Remote First", description: "Work from anywhere in the world" },
    { icon: Heart, title: "Health & Wellness", description: "Comprehensive health coverage" },
    { icon: TrendingUp, title: "Equity", description: "Ownership in the company" },
    { icon: Coffee, title: "Unlimited PTO", description: "Take time when you need it" },
    { icon: Laptop, title: "Equipment", description: "Latest tech setup provided" },
    { icon: Users, title: "Learning Budget", description: "$2,000 annual learning stipend" },
  ];

  const values = [
    {
      icon: Shield,
      title: "Safety First",
      description: "We prioritize the safety of our users and community above all else.",
    },
    {
      icon: Zap,
      title: "Move Fast",
      description: "We ship quickly, iterate often, and aren't afraid to experiment.",
    },
    {
      icon: Heart,
      title: "User Obsessed",
      description: "Every decision starts with the question: how does this help our users?",
    },
    {
      icon: Users,
      title: "Team Players",
      description: "We win together and support each other through challenges.",
    },
  ];

  const openPositions = [
    {
      title: "Senior Backend Engineer",
      department: "Engineering",
      location: "Remote",
      type: "Full-time",
    },
    {
      title: "Product Designer",
      department: "Design",
      location: "Remote",
      type: "Full-time",
    },
    {
      title: "Trust & Safety Analyst",
      department: "Operations",
      location: "Remote",
      type: "Full-time",
    },
    {
      title: "Frontend Engineer",
      department: "Engineering",
      location: "Remote",
      type: "Full-time",
    },
    {
      title: "Machine Learning Engineer",
      department: "Engineering",
      location: "Remote",
      type: "Full-time",
    },
    {
      title: "Customer Support Lead",
      department: "Support",
      location: "Remote",
      type: "Full-time",
    },
    {
      title: "Marketing Manager",
      department: "Marketing",
      location: "San Francisco / Remote",
      type: "Full-time",
    },
    {
      title: "Data Analyst",
      department: "Data",
      location: "Remote",
      type: "Full-time",
    },
  ];

  const departments = [...new Set(openPositions.map(p => p.department))];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <nav className="border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="container flex items-center justify-between h-16">
          <Link href="/">
            <a className="text-2xl font-bold gradient-text">{t('common.appName')}</a>
          </Link>
          <div className="flex items-center gap-4">
            <Link href="/">
              <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="w-4 h-4" />
                {t('common.back')}
              </a>
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      <main className="flex-1">
        {/* Hero */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
          <div className="container relative text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-6">
              <Briefcase className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Join Our <span className="gradient-text">Mission</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Help us build the world's most trusted marketplace. We're looking for passionate 
              people who want to make a difference.
            </p>
            <a href="#positions" className="btn-primary inline-flex items-center gap-2">
              View Open Positions
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-muted/30">
          <div className="container">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-primary mb-2">150+</div>
                <div className="text-muted-foreground">Team Members</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">30+</div>
                <div className="text-muted-foreground">Countries</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">100%</div>
                <div className="text-muted-foreground">Remote</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">4.8</div>
                <div className="text-muted-foreground">Glassdoor Rating</div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Values</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                These principles guide everything we do, from product decisions to how we 
                treat each other.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {values.map((value) => (
                <div key={value.title} className="card-elegant p-6 text-center">
                  <value.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 bg-muted/30">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Benefits & Perks</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We believe in taking care of our team. Here's what you can expect when you join us.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {benefits.map((benefit) => (
                <div key={benefit.title} className="card-elegant p-6 flex items-start gap-4">
                  <div className="bg-primary/10 rounded-lg p-3">
                    <benefit.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section id="positions" className="py-16">
          <div className="container max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Open Positions</h2>
              <p className="text-muted-foreground">
                {openPositions.length} open positions across {departments.length} departments
              </p>
            </div>

            <div className="space-y-4">
              {openPositions.map((position, index) => (
                <div
                  key={index}
                  className="card-elegant p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div>
                    <h3 className="font-semibold text-lg mb-1">{position.title}</h3>
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {position.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {position.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {position.type}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" className="shrink-0">
                    Apply Now
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-muted-foreground mb-4">
                Don't see a role that fits? We're always looking for talented people.
              </p>
              <a
                href="mailto:careers@listomarket.app"
                className="btn-secondary inline-flex items-center gap-2"
              >
                Send General Application
              </a>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-muted/30">
          <div className="container text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Make an Impact?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join a team that's building the future of online marketplaces. We can't wait to 
              meet you.
            </p>
            <a href="#positions" className="btn-primary inline-flex items-center gap-2">
              Browse All Positions
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
